import React, { useId } from "react";
import { Box, Button, InputText, useModals, Stack, Text, Dialog } from "@gusarov-studio/rubik-ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRenameResourceMutation } from "..";
import { fileTypes } from "../../../common/constants/fileTypes";
import { type Database } from "../../../supabase/supabaseTypes";

interface RenameResourceDilogContainerProps {
  resource: Database["public"]["Tables"]["resources"]["Row"];
}

const FormSchema = z.object({
  name: z.string().trim().nonempty("Name must not be empty"),
});

type FormData = z.infer<typeof FormSchema>;

const RenameResourceDilogContainer: React.FC<RenameResourceDilogContainerProps> = ({
  resource,
}) => {
  "use memo";
  const modals = useModals();

  const closeDialog = () => {
    modals.close();
  };

  const [renameResource, { isLoading: isRenaming }] = useRenameResourceMutation();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    defaultValues: {
      name: resource.name,
    },
    mode: "onTouched",
    resolver: zodResolver(FormSchema),
  });

  const rename = (data: FormData) => {
    renameResource({
      resourceId: resource.id,
      name: data.name,
    });
    closeDialog();
  };

  const nameFieldId = useId();
  const formId = useId();

  let captions;

  switch (resource.type) {
    case fileTypes.PROMPT:
      captions = {
        title: "Rename",
        description: "Please give a new name for your prompt",
        fieldTitle: "Prompt name",
      };
      break;
    case fileTypes.CHAT:
      captions = {
        title: "Rename",
        description: "Please give a new name for your chat",
        fieldTitle: "Chat name",
      };
      break;
    default:
      captions = {
        title: "Rename",
        description: "Please give a new name for your folder",
        fieldTitle: "Folder name",
      };
  }

  return (
    <Dialog.Root open={true}>
      <Dialog.Content width="400px">
        <Dialog.Decoration type="grid" />
        <Dialog.Close
          onClick={closeDialog}
          disabled={isRenaming}
        />
        <Dialog.Header>
          <Dialog.Title>{captions.title}</Dialog.Title>

          <Dialog.Description>{captions.description}</Dialog.Description>
        </Dialog.Header>

        <Box padding="20px 24px 32px">
          <form
            id={formId}
            onSubmit={handleSubmit(rename)}
          >
            <Stack spacing={4}>
              <Text
                as="label"
                htmlFor="name"
                weight="500"
                size="14"
                color="primary"
              >
                {captions.fieldTitle}
              </Text>

              <InputText
                id={nameFieldId}
                invalid={Boolean(errors.name)}
                disabled={isRenaming}
                {...register("name")}
              />

              {errors.name?.message && (
                <Text
                  color="danger"
                  size="14"
                >
                  {errors.name?.message}
                </Text>
              )}
            </Stack>
          </form>
        </Box>

        <Dialog.Footer>
          <Button
            appearance="outline"
            variant="secondary"
            size="40"
            onClick={closeDialog}
            disabled={isRenaming}
          >
            Cancel
          </Button>

          <Button
            variant="accent"
            size="40"
            type="submit"
            disabled={isRenaming}
            form={formId}
          >
            Submit
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export { RenameResourceDilogContainer };
